// What a coupon leaves you paying, worked out from the price WE display.
//
// The backend's `finalPrice` cannot be used for the Overwolf plans:
// `checkPromoCode` is called with the code alone (`http-req.ts` - no product id),
// so whatever it prices the discount against is not necessarily the product the
// user is about to buy. A "$5 off" coupon priced against the $5 web product comes
// back as 0, which on a $15.00 Streamer card would read "Free" and be a lie.
//
// So the card computes it from the two things it can trust: the price on the card
// and the coupon's own reduction. That is also the arithmetic Stripe performs
// against the product being bought, so the number shown and the number charged
// agree.

import type { Coupon } from '../../interfaces/coupon';

/**
 * Returns the price to show after a coupon, or null when the coupon buys nothing.
 *
 * `basePrice` is the card's own price as a string ('15.00'). `isReductionAmount`
 * decides how `reduction` is read: dollars off when true, a percentage when false.
 */
export function discountedPrice(basePrice: string, coupon: Coupon | null): string | null {
  if (!coupon || coupon.price == null) return null;

  const base = Number(basePrice);
  const reduction = Number(coupon.reduction);
  if (!Number.isFinite(base) || !Number.isFinite(reduction)) return null;

  const final = coupon.isReductionAmount ? base - reduction : base * (1 - reduction / 100);

  // A discount that meets or exceeds the price is free, not a negative number -
  // and a rounding error must never turn "free" into "$0.00 to pay".
  if (final <= 0.005) return 'Free';

  return `$${final.toFixed(2)}`;
}
